/* HomePage.jsx — preserves the original Panchangam UI pixel-perfect.
   All CSS is in HomePage.css. The vanilla-JS animation / theme / kala logic
   from the original template is kept unchanged, wrapped inside a React effect
   so it runs after the DOM mounts. */

import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./HomePage.css";
import { useDocumentMeta } from "../hooks/use-document-meta";
import { useLanguage } from "../contexts/LanguageContext";
import MuhurtamChecker from "../components/MuhurtamChecker";
import KundaliniLookup from "../components/KundaliniLookup";
import { fetchPanchangam, fetchFestivalCalendar } from "../lib/api";
import { getSpecialKalas, parseKalaTimeSafe, checkTimeInRange } from "../lib/panchangamHelpers";
import { findNearestCity } from "../lib/cities";
import { findByPincode } from "../lib/pincodes";
import LiveLocationSearch from "../components/LiveLocationSearch";
import MapPicker from "../components/MapPicker";

// Matches the backend's own fallback default (settings.py DEFAULT_LATITUDE/
// LONGITUDE) — used only to resolve a display name when coords hasn't been
// set yet, never sent to the API (the backend applies its own default then).
const FALLBACK_COORDS = { latitude: 17.3850, longitude: 78.4867 };

// "Get Panchangam" opens the animated book modal when true, or the
// palm-leaf modal (no opening animation) when false. Book logic stays in
// place further down — this just decides which of the two gets rendered.
const BOOK_MODAL_ACTIVE = false;

// Festival search matches against every language's name, not just the
// currently displayed one — a Telugu-language user typing in English (or
// any other language) should still find the festival, regardless of which
// language it's shown in.
const festivalMatchesQuery = (festival, query) => {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  return Object.values(festival.name).some((name) => name.toLowerCase().includes(q));
};

export default function HomePage() {
  useDocumentMeta(
    "Yagnika Panchangam (Panchanga) — Daily Telugu Hindu Calendar, Tithi, Nakshatra & Muhurtam",
    "Yagnika Panchangam (Panchanga) — daily Hindu Panchangam with tithi, nakshatra, auspicious Muhurtam timings, festival calendar and Kundalini chart, for your location in English, Telugu, Hindi, Tamil, Kannada & Malayalam."
  );
  const { lang, setLanguage, t, dict } = useLanguage();
  const [panchangam, setPanchangam] = useState(null);
  // The day before selectedDate: its after-midnight "(+1)" kalas (e.g. a
  // 4:30 AM Varjyam) are still running before today's sunrise.
  const [prevPanchangam, setPrevPanchangam] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });
  const calendarType = "amanta";
  const [modalOpen, setModalOpen] = useState(false);
  // null = not resolved yet / permission denied — backend then falls back to
  // its own default (Hyderabad). Set once if the browser's Geolocation API
  // grants access; never overwritten afterward.
  const [coords, setCoords] = useState(null);
  const [placeName, setPlaceName] = useState(null);
  const [locPickerOpen, setLocPickerOpen] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState("");
  const [pincodeInput, setPincodeInput] = useState("");
  const [mapPickerOpen, setMapPickerOpen] = useState(false);
  const [festivalPickerOpen, setFestivalPickerOpen] = useState(false);
  const [festivalList, setFestivalList] = useState(null);
  const [festivalListLoading, setFestivalListLoading] = useState(false);
  const [selectedFestivalKey, setSelectedFestivalKey] = useState("");
  const [festivalSearchQuery, setFestivalSearchQuery] = useState("");
  const [showGuideToast, setShowGuideToast] = useState(false);
  const [muhurtamOpen, setMuhurtamOpen] = useState(false);
  const [kundaliniOpen, setKundaliniOpen] = useState(false);

  // Only one of the two panels shown at a time — they're opened from
  // separate buttons now (not stacked FABs), so there's no fixed screen
  // real estate reserved for both to be open together.
  const toggleMuhurtam = () => {
    setMuhurtamOpen((o) => !o);
    setKundaliniOpen(false);
  };
  const toggleKundalini = () => {
    setKundaliniOpen((o) => !o);
    setMuhurtamOpen(false);
  };

  const rafRef = useRef(null);
  const timeoutRef = useRef(null);
  const isOpeningRef = useRef(false);

  const useMyLocation = () => {
    setLocationError("");
    if (!navigator.geolocation) {
      setLocationError(t("muhurtamLocationError"));
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          tzOffsetMin: -new Date().getTimezoneOffset(),
        });
        setLocating(false);
        setLocPickerOpen(false);
      },
      () => {
        setLocationError(t("muhurtamLocationError"));
        setLocating(false);
      },
      { timeout: 8000, maximumAge: 600000 }
    );
  };

  const selectCity = (city) => {
    setCoords({ latitude: city.lat, longitude: city.lon, tzOffsetMin: city.tzOffsetMin });
    setLocationError("");
    setLocPickerOpen(false);
  };

  const submitPincode = (e) => {
    e?.preventDefault?.();
    const hit = findByPincode(pincodeInput);
    if (!hit) {
      setLocationError(t("locPincodeNotFound"));
      return;
    }
    setCoords({ latitude: hit.lat, longitude: hit.lon, tzOffsetMin: hit.tzOffsetMin });
    setLocationError("");
    setPincodeInput("");
    setLocPickerOpen(false);
  };

  const confirmMapPick = ({ lat, lon }) => {
    setCoords({ latitude: lat, longitude: lon, tzOffsetMin: -new Date().getTimezoneOffset() });
    setLocationError("");
    setMapPickerOpen(false);
    setLocPickerOpen(false);
  };

  // Lazily fetches the full-year festival list (name + date + tithi timing
  // for every festival) the first time the picker is opened, then reuses it
  // — re-fetches only if the active year or location actually changes.
  const toggleFestivalPicker = async () => {
    const willOpen = !festivalPickerOpen;
    setFestivalPickerOpen(willOpen);
    if (!willOpen) return;
    const year = new Date(selectedDate).getFullYear();
    if (festivalList && festivalList.year === year && festivalList.coords === coords) return;
    setFestivalListLoading(true);
    try {
      const data = await fetchFestivalCalendar(year, coords);
      setFestivalList({ year, coords, items: data.festivals });
    } catch (e) {
      console.error("festival calendar fetch failed", e);
      setFestivalList({ year, coords, items: [] });
    } finally {
      setFestivalListLoading(false);
    }
  };

  // Ask the browser for the user's location once, on first load. If granted,
  // sunrise/sunset/moonrise/moonset (and the sky animation derived from them)
  // switch to the user's real location; if denied/unavailable, the backend's
  // Hyderabad default keeps things working exactly as before. The user can
  // still change this afterward via the location picker (search or re-detect).
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          tzOffsetMin: -new Date().getTimezoneOffset(),
        });
      },
      () => {
        // Permission denied or unavailable — leave coords null (Hyderabad default).
      },
      { timeout: 8000, maximumAge: 600000 }
    );
  }, []);

  // Resolve a display name for whichever coordinates are currently active
  // (GPS, picked city, or the Hyderabad fallback) so the location is always
  // visible, not silent.
  useEffect(() => {
    const c = coords || FALLBACK_COORDS;
    const nearest = findNearestCity(c.latitude, c.longitude);
    if (!nearest) setPlaceName(null);
    else if (nearest.distanceKm < 5) setPlaceName(nearest.name);
    else if (nearest.distanceKm < 75) setPlaceName(`${t("locNear")} ${nearest.name}`);
    else setPlaceName(t("locCustom"));
  }, [coords, t]);

  // First-time visitors (per browser) get a brief, self-dismissing nudge
  // toward the User Guide — shown once, never again after that.
  useEffect(() => {
    if (localStorage.getItem("yagnika_guide_seen")) return;
    localStorage.setItem("yagnika_guide_seen", "1");
    setShowGuideToast(true);
    const timer = setTimeout(() => setShowGuideToast(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  // Load panchangam whenever date, calendar type, or resolved location changes
  useEffect(() => {
    (async () => {
      try {
        const data = await fetchPanchangam(selectedDate, calendarType, coords);
        setPanchangam(data);
      } catch (e) {
        console.error("panchangam fetch failed", e);
      }
    })();
    (async () => {
      setPrevPanchangam(null);
      const [y, m, d] = selectedDate.split("-").map(Number);
      const prev = new Date(y, m - 1, d - 1);
      const prevStr = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, "0")}-${String(prev.getDate()).padStart(2, "0")}`;
      try {
        setPrevPanchangam(await fetchPanchangam(prevStr, calendarType, coords));
      } catch (e) {
        // Only used to highlight pre-sunrise kalas; safe to go without.
      }
    })();
  }, [selectedDate, calendarType, coords]);

  // Render panchangam values into the DOM + build kala wheel (mirrors original logic).
  useEffect(() => {
    if (!panchangam) return;
    renderPanchangam(panchangam, lang, dict);
    if (modalOpen) populateModal(panchangam, lang, dict);
  }, [panchangam, lang, dict, modalOpen]);

  // Palm-leaf columns fade their top/bottom edge to hint there's more to
  // scroll — but the fade must only show on the edge that actually has more
  // content, not the true start/end (a fixed CSS mask faded the real last
  // line too, which looked like content was being cut off). Recomputed on
  // every scroll and whenever populateModal above replaces the content
  // (innerHTML resets scrollTop to 0, so this re-syncs to "at top" then).
  useEffect(() => {
    if (BOOK_MODAL_ACTIVE || !modalOpen) return;
    const els = ["panchangamLeftPage", "panchangamRightPage"]
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    const update = (el) => {
      const atTop = el.scrollTop <= 1;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
      const mask = `linear-gradient(to bottom, ${
        atTop ? "#000 0%" : "transparent 0%, #000 26px"
      }, ${atBottom ? "#000 100%" : "#000 calc(100% - 26px), transparent 100%"})`;
      el.style.maskImage = mask;
      el.style.webkitMaskImage = mask;
    };

    const cleanups = els.map((el) => {
      const handler = () => update(el);
      el.addEventListener("scroll", handler);
      update(el);
      return () => el.removeEventListener("scroll", handler);
    });
    return () => cleanups.forEach((fn) => fn());
  }, [modalOpen, panchangam, lang, dict, calendarType]);

  // Real-time celestial cycle + theme (uses requestAnimationFrame like original)
  useEffect(() => {
    function loop() {
      if (panchangam) updateCelestialCycle(panchangam, lang, dict, prevPanchangam);
      rafRef.current = requestAnimationFrame(loop);
    }
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [panchangam, prevPanchangam, lang, dict]);

  // Real-time clock
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const timeStr = now.toLocaleTimeString("en-US", { hour12: true, hour: "2-digit", minute: "2-digit" });
      const dateStr = now.toLocaleDateString("en-US", { day: "2-digit", month: "short" });
      const el = document.getElementById("clockTime");
      const el2 = document.getElementById("clockDate");
      if (el) el.innerText = timeStr;
      if (el2) el2.innerText = dateStr;
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  // Scale the modal canvas to viewport, matching original behaviour.
  // Below 768px wide we fit strictly by width so the full book is always
  // visible with no horizontal clipping, and let the modal scroll vertically
  // for whatever doesn't fit on screen.
  useEffect(() => {
    const scale = () => {
      const wrapper = document.getElementById("yag-canvas-wrapper");
      const splash = document.getElementById("yag-splash-screen");
      const bottomBar = document.querySelector(".bottom-bar");
      // On desktop the canvas is centered in the full modal height, but
      // .bottom-bar is pinned separately to the viewport's bottom edge —
      // sizing purely by window.innerHeight let the canvas's own bottom
      // edge land underneath the bar instead of above it. Reserving the
      // bar's real rendered height (plus a small gap) keeps the two from
      // overlapping.
      const barHeight = bottomBar ? bottomBar.getBoundingClientRect().height : 0;
      const scaleX = window.innerWidth / 1672;
      const scaleY = (window.innerHeight - barHeight - 16) / 941;
      const isNarrow = window.innerWidth < 768;
      const s = isNarrow ? scaleX : Math.min(scaleX, scaleY);
      if (wrapper) wrapper.style.transform = `scale(${s})`;
      if (splash) splash.style.transform = `scale(${s})`;
    };
    scale();
    window.addEventListener("resize", scale);
    return () => window.removeEventListener("resize", scale);
  }, [modalOpen]);

  // -------- modal / navigation handlers (preserving exact animation timings) --------
  // Animated book-opening modal — kept fully intact (not deleted) but disabled
  // via BOOK_MODAL_ACTIVE above; it was the source of a reported opening
  // glitch. The palm-leaf modal below is the active replacement. Flip
  // BOOK_MODAL_ACTIVE back to true (and the JSX branch further down) to
  // restore this.
  const openBookModal = () => {
    setModalOpen(true);
    isOpeningRef.current = false;
    setTimeout(() => {
      const splash = document.getElementById("yag-splash-screen");
      const wrapper = document.getElementById("yag-canvas-wrapper");
      const leftBox = document.getElementById("panchangamLeftPage");
      const rightBox = document.getElementById("panchangamRightPage");
      if (!splash) return;

      // Reset to a clean slate every single time this runs — first open or
      // the tenth — so the previous session's fully-open book background can
      // never be left behind to flash before the splash covers it.
      wrapper.style.backgroundImage = "none";
      leftBox.style.opacity = "0";
      rightBox.style.opacity = "0";
      splash.style.backgroundImage = `url('/static/PanchangamOpening.webp?v=${Date.now()}')`;
      splash.style.display = "block";
      splash.style.opacity = "1";

      timeoutRef.current = setTimeout(() => {
        // Instant cut, no fade: swap splash -> open book in the same tick so
        // there's no partial-opacity glitch between the two images.
        splash.style.display = "none";
        wrapper.style.backgroundImage = "url('/static/PanchangamOpenFull.webp')";
        if (panchangam) populateModal(panchangam, lang, dict);
        leftBox.style.opacity = "1";
        rightBox.style.opacity = "1";
        isOpeningRef.current = true;
        timeoutRef.current = null;
      }, 7000);
    }, 30);
  };

  // Palm-leaf modal — no opening animation, just shows the modal and lets
  // the existing populateModal effect (below, keyed on modalOpen) fill in
  // #panchangamLeftPage / #panchangamRightPage once they're mounted.
  const openPalmLeaf = () => {
    setModalOpen(true);
    isOpeningRef.current = true;
  };

  const openFullPanchangam = BOOK_MODAL_ACTIVE ? openBookModal : openPalmLeaf;

  const closeModal = () => {
    setModalOpen(false);
    isOpeningRef.current = false;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const navigateModalDay = async (offset) => {
    if (!isOpeningRef.current && offset !== 0) return;
    // No page-turn animation — the date changes and populateModal re-renders
    // the already-open pages in place via the existing effects.
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + offset);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    setSelectedDate(`${y}-${m}-${day}`);
  };

  return (
    <div className="yag-root">
      {/* Full panchangam modal — animated book (kept, currently disabled) or palm leaf (active) */}
      {BOOK_MODAL_ACTIVE ? (
        <div id="panchangamModal" className="yag-modal" style={{ display: modalOpen ? "flex" : "none" }}>
          <span className="close-modal-btn" data-testid="close-modal-btn" onClick={closeModal}>&times;</span>
          <div id="yag-splash-screen"></div>
          <div id="yag-canvas-wrapper">
            <div className="page-box left-page-modal" id="panchangamLeftPage" onClick={() => navigateModalDay(-1)} style={{ cursor: "pointer" }} />
            <div className="page-box right-page-modal" id="panchangamRightPage" onClick={() => navigateModalDay(1)} style={{ cursor: "pointer" }} />
          </div>
        </div>
      ) : (
        <div id="panchangamModal" className="yag-modal palm-leaf-modal" style={{ display: modalOpen ? "flex" : "none" }}>
          <span className="close-modal-btn" data-testid="close-modal-btn" onClick={closeModal}>&times;</span>
          <div id="yag-canvas-wrapper">
            <div className="palm-leaf-card">
              <div className="palm-leaf-cover"></div>
              <div className="palm-leaf-columns">
                <div className="palm-leaf-col" id="panchangamLeftPage" />
                <div className="palm-leaf-divider"></div>
                <div className="palm-leaf-col" id="panchangamRightPage" />
              </div>
              <div className="palm-leaf-cover"></div>
            </div>
          </div>
        </div>
      )}

      <div className="sky-viewport">
        <div className="bg-layer bg-morning active" id="bgMorning" style={{ backgroundImage: "url('/static/HomePage_Morning.webp')" }}></div>
        <div className="bg-layer bg-afternoon" id="bgAfternoon" style={{ backgroundImage: "url('/static/HomePage_Afternoon.webp')" }}></div>
        <div className="bg-layer bg-evening" id="bgEvening" style={{ backgroundImage: "url('/static/HomePage_Evening.webp')" }}></div>
        <div className="bg-layer bg-night" id="bgNight" style={{ backgroundImage: "url('/static/HomePage_Night.webp')" }}></div>

        <nav className="main-navbar" id="mainNavbar">
          <div className="brand-container">
            <a href="#" className="brand-logo"><img src="/static/Logo.webp" alt="Yagnika" className="logo-img" /></a>
          </div>

          <div className="nav-timings" id="navTimings">
            <span className="timing-item" title="Sunrise"><span id="label_sunrise"></span><img src="/static/Sunrise_Icon.webp" alt="Sunrise" className="celestial-icon" /> : <span id="nav-sunrise">--:--</span></span>
            <span className="timing-item" title="Sunset"><span id="label_sunset"></span><img src="/static/Sunset_Icon.webp" alt="Sunset" className="celestial-icon" /> : <span id="nav-sunset">--:--</span></span>
            <span className="timing-item" title="Moonrise"><span id="label_moonrise"></span><img src="/static/Moonrise_Icon.webp" alt="Moonrise" className="celestial-icon" /> : <span id="nav-moonrise">--:--</span></span>
            <span className="timing-item" title="Moonset"><span id="label_moonset"></span><img src="/static/Moonset_Icon.webp" alt="Moonset" className="celestial-icon" /> : <span id="nav-moonset">--:--</span></span>
          </div>

          <ul className="nav-links">
            <li className="loc-dropdown">
              <button
                className="loc-dropdown-btn"
                data-testid="loc-btn"
                onClick={() => setLocPickerOpen((o) => !o)}
              >
                📍 {placeName || "…"}
              </button>
              {locPickerOpen && (
                <div className="loc-dropdown-content">
                  <LiveLocationSearch
                    placeholder={t("muhurtamCitySearch")}
                    className="loc-city-input"
                    onSelect={selectCity}
                    t={t}
                  />
                  <button type="button" className="loc-use-btn" onClick={useMyLocation} disabled={locating}>
                    {locating ? t("muhurtamLocating") : t("muhurtamUseLocation")}
                  </button>
                  <form className="loc-secondary-row" onSubmit={submitPincode}>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      className="loc-pincode-input"
                      placeholder={t("locPincodePlaceholder")}
                      value={pincodeInput}
                      onChange={(e) => setPincodeInput(e.target.value.replace(/\D/g, ""))}
                    />
                    <button type="submit" className="loc-use-btn" style={{ width: "auto" }}>{t("locPincode")}</button>
                  </form>
                  <button type="button" className="loc-map-btn" onClick={() => setMapPickerOpen(true)}>
                    {t("locPickMap")}
                  </button>
                  {locationError && <div className="loc-error">{locationError}</div>}
                </div>
              )}
            </li>
            <li className="festival-dropdown">
              <button
                className="festival-dropdown-btn"
                data-testid="festival-btn"
                onClick={toggleFestivalPicker}
              >
                {t("festivalPickerBtn")}
              </button>
              {festivalPickerOpen && (
                <div className="festival-dropdown-content">
                  {festivalListLoading ? (
                    <div className="festival-loading">…</div>
                  ) : (
                    <>
                      <input
                        type="text"
                        className="festival-search-input"
                        data-testid="festival-search"
                        placeholder={t("festivalSearchPlaceholder")}
                        value={festivalSearchQuery}
                        onChange={(e) => setFestivalSearchQuery(e.target.value)}
                      />
                      <select
                        className="festival-select"
                        data-testid="festival-select"
                        value={selectedFestivalKey}
                        onChange={(e) => setSelectedFestivalKey(e.target.value)}
                        size={festivalSearchQuery ? Math.min(8, Math.max(2, (festivalList?.items || []).filter((f) => festivalMatchesQuery(f, festivalSearchQuery)).length + 1)) : undefined}
                      >
                        <option value="">{t("festivalPickerPlaceholder")}</option>
                        {(festivalList?.items || [])
                          .filter((f) => festivalMatchesQuery(f, festivalSearchQuery))
                          .map((f) => (
                            <option key={f.key} value={f.key}>{f.name[lang]}</option>
                          ))}
                      </select>
                      {selectedFestivalKey && (() => {
                        const f = (festivalList?.items || []).find((x) => x.key === selectedFestivalKey);
                        if (!f) return null;
                        const datesLabel = f.dates
                          .map((iso) => new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }))
                          .join(" & ");
                        return (
                          <div className="festival-result" data-testid="festival-result">
                            <div className="festival-result-name">{f.name[lang]}</div>
                            <div className="festival-result-row">
                              <span className="festival-result-label">{t("festivalPickerDateLabel")}</span>
                              <span>{datesLabel}</span>
                            </div>
                            {f.tithi_start && (
                              <div className="festival-result-row">
                                <span className="festival-result-label">{t("festivalPickerTimeLabel")}</span>
                                <span>{f.tithi_start} – {f.tithi_end}</span>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </>
                  )}
                </div>
              )}
            </li>
            <li>
              <Link to="/guide" data-testid="guide-link">{t("guideBtn")}</Link>
            </li>
            <li className="lang-dropdown">
              <button className="lang-dropdown-btn" id="langBtn" data-testid="lang-btn">{t("langBtn")}</button>
              <div className="lang-dropdown-content">
                <button data-testid="lang-en" onClick={() => setLanguage("en")}>English</button>
                <button data-testid="lang-te" onClick={() => setLanguage("te")}>తెలుగు (Telugu)</button>
                <button data-testid="lang-hi" onClick={() => setLanguage("hi")}>हिन्दी (Hindi)</button>
                <button data-testid="lang-ta" onClick={() => setLanguage("ta")}>தமிழ் (Tamil)</button>
                <button data-testid="lang-kn" onClick={() => setLanguage("kn")}>ಕನ್ನಡ (Kannada)</button>
                <button data-testid="lang-ml" onClick={() => setLanguage("ml")}>മലയാളം (Malayalam)</button>
              </div>
            </li>
          </ul>
        </nav>

        {showGuideToast && (
          <Link to="/guide" className="guide-toast" data-testid="guide-toast">
            {t("guideToastText")}
          </Link>
        )}

        <div className="celestial-sky-box" id="celestialSkyBox">
          <div className="sun" id="sunIcon"></div>
          <div className="moon-container" id="moonContainer">
            <div className="moon-body">
              <div className="moon-shadow" id="moonShadow"></div>
            </div>
          </div>
        </div>

        <div className="main-wrapper">
          <div className="content-row">
            <div className="left-spacer"></div>
            <div className="left-column">
              <div className="panchangam-book day-theme" id="panchangamCard">
                <div className="book-corner top-left"></div>
                <div className="book-corner top-right"></div>
                <div className="book-corner bottom-left"></div>
                <div className="book-corner bottom-right"></div>
                <div className="book-page left-page">
                  <h1 className="main-heading" id="greetingHeading">{t("goodMorning")}</h1>
                  <h2 className="sub-heading" id="subHeading">{t("subHeading")}</h2>
                  <div className="date-badge" id="todayDateDisplay"></div>
                  <img src="/static/Om_Logo.webp" alt="Om" className="om-logo-img" />
                  <div className="book-shloka">
                    <div className="book-shloka-divider">❖</div>
                    <div className="book-shloka-text">
                      {t("bookShlokaText").split("\n").map((line, i, arr) => (
                        <React.Fragment key={i}>
                          {line}
                          {i < arr.length - 1 && <br />}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="book-page right-page">
                  <div className="panchangam-details">
                    <div className="panchangam-row"><span className="panchangam-label" id="lbl-samvatsaram">{t("lblSamvatsaram")}</span><span className="panchangam-value" id="val-samvatsaram" data-testid="val-samvatsaram">...</span></div>
                    <div className="panchangam-row"><span className="panchangam-label" id="lbl-ayanam">{t("lblAyanam")}</span><span className="panchangam-value" id="val-ayanam">...</span></div>
                    <div className="panchangam-row"><span className="panchangam-label" id="lbl-rithuvu">{t("lblRithuvu")}</span><span className="panchangam-value" id="val-rithuvu">...</span></div>
                    <div className="panchangam-row"><span className="panchangam-label" id="lbl-masam">{t("lblMasam")}</span><span className="panchangam-value" id="val-masam">...</span></div>
                    <div className="panchangam-row"><span className="panchangam-label" id="lbl-paksham">{t("lblPaksham")}</span><span className="panchangam-value" id="val-paksham">...</span></div>
                    <div className="panchangam-row"><span className="panchangam-label" id="lbl-thidhi">{t("lblThidhi")}</span><span className="panchangam-value" id="val-thidhi" data-testid="val-thidhi">...</span></div>
                    <div className="panchangam-row"><span className="panchangam-label" id="lbl-varam">{t("lblVaram")}</span><span className="panchangam-value" id="val-varam" data-testid="val-varam">...</span></div>
                    <div className="panchangam-row"><span className="panchangam-label" id="lbl-nakshatram">{t("lblNakshatram")}</span><span className="panchangam-value" id="val-nakshatram">...</span></div>
                  </div>
                </div>
              </div>

              <div className="panchangam-actions">
                <input type="date" id="selectedDate" data-testid="selected-date" className="date-picker"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)} />
                <button data-testid="btn-full-panchangam" className="btn-full-panchangam" onClick={openFullPanchangam}>{t("btnFullPanchangam")}</button>
              </div>

              <div className="panchangam-quick-actions">
                <button
                  type="button"
                  data-testid="muhurtam-open-btn"
                  className="btn-full-panchangam btn-quick-action"
                  onClick={toggleMuhurtam}
                >
                  {t("muhurtamTitle")}
                </button>
                <button
                  type="button"
                  data-testid="kundalini-open-btn"
                  className="btn-full-panchangam btn-quick-action"
                  onClick={toggleKundalini}
                >
                  {t("kundaliniFabLabel")}
                </button>
              </div>
            </div>

            <div className="right-column">
              <div className="panchangam-clock-container">
                <div className="outer-brass-ring"></div>
                <div className="outer-wheel" id="outerWheel"></div>
                <div className="inner-wheel" id="innerWheel">
                  <div className="digital-time" id="clockTime" style={{ fontSize: "clamp(12px, 1.6vw, 18px)", color: "#fff", marginBottom: 4 }}>--:--:--</div>
                  <div className="date-display" id="clockDate">-- ---</div>
                  <div className="date-display" id="kalaChakraLabel">{t("kalaChakram")}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <MuhurtamChecker open={muhurtamOpen} onClose={() => setMuhurtamOpen(false)} />
      <KundaliniLookup open={kundaliniOpen} onClose={() => setKundaliniOpen(false)} />
      {mapPickerOpen && (
        <MapPicker
          initialLat={coords?.latitude}
          initialLon={coords?.longitude}
          onPick={confirmMapPick}
          onClose={() => setMapPickerOpen(false)}
          t={t}
        />
      )}
    </div>
  );
}

/* ================ Helpers (ported from original vanilla JS) ================ */

// getSpecialKalas now lives in ../lib/panchangamHelpers.js

function renderPanchangam(data, lang, dict) {
  const set = (id, txt) => { const el = document.getElementById(id); if (el) el.innerText = txt; };
  set("label_sunrise", dict[lang].sunrise + " ");
  set("label_sunset", dict[lang].sunset + " ");
  set("label_moonrise", dict[lang].moonrise + " ");
  set("label_moonset", dict[lang].moonset + " ");
  set("nav-sunrise", data.solar.sunrise);
  set("nav-sunset", data.solar.sunset);
  set("nav-moonrise", data.lunar.moonrise);
  set("nav-moonset", data.lunar.moonset);
  set("val-samvatsaram", data.general.samvatsara[lang]);
  set("val-ayanam", data.general.ayanam[lang]);
  set("val-rithuvu", data.general.ritu[lang]);
  set("val-masam", data.general.masa[lang]);
  set("val-paksham", data.general.paksha[lang]);
  set("val-thidhi", `${data.general.tithi[lang]} ${data.general.tithi_till[lang]}, ${dict[lang].extThen} ${data.general.tithi_next[lang]}`);
  set("val-varam", data.general.varam[lang]);
  set("val-nakshatram", `${data.general.nakshatra[lang]} ${data.general.nakshatra_till[lang]}`);
  const festivals = data.extended?.festivals || [];
  const festivalPart = festivals.length ? ` • \u{1F549}\u{FE0F} ${festivals.map(f => f.name[lang]).join(", ")}` : "";
  set("todayDateDisplay", data.date + festivalPart);
  set("kalaChakraLabel", dict[lang].kalaChakram);
  renderKalas(getSpecialKalas(data), lang);
}

function renderKalas(kalas, lang) {
  const outerWheel = document.getElementById("outerWheel");
  if (!outerWheel) return;
  outerWheel.querySelectorAll(".time-spoke").forEach(s => s.remove());
  kalas.forEach((kala, index) => {
    const angle = index * (360 / kalas.length);
    const spoke = document.createElement("div");
    spoke.className = "time-spoke";
    const auspiciousIds = ["brahma_muhurta", "abhijit_muhurta", "amrita_kala"];
    spoke.classList.add(auspiciousIds.includes(kala.id) ? "auspicious" : "inauspicious");
    spoke.dataset.id = kala.id;
    spoke.style.transform = `rotate(${angle}deg)`;

    const content = document.createElement("div");
    content.className = "upright-content";
    content.style.transform = `rotate(-${angle}deg)`;

    const title = document.createElement("span");
    title.className = "label-title";
    const rawName = kala.name[lang];
    const nameParts = rawName.split(" ");
    if (nameParts.length === 2) title.innerHTML = `${nameParts[0]}<br>${nameParts[1]}`;
    else if (nameParts.length > 2) title.innerHTML = `${nameParts[0]}<br>${nameParts.slice(1).join(" ")}`;
    else title.innerText = rawName;

    const time = document.createElement("span");
    time.className = "label-time";
    time.innerHTML = kala.time.split(" / ").join("<br>");

    content.appendChild(title);
    content.appendChild(time);
    spoke.appendChild(content);
    outerWheel.appendChild(spoke);
  });
}

// parseKalaTimeSafe / checkTimeInRange now live in ../lib/panchangamHelpers.js

function updateCelestialCycle(panchangam, lang, dict, prevPanchangam = null) {
  const now = new Date();
  const windowWidth = window.innerWidth;
  const celestialWidth = 80;
  const startX = -celestialWidth;
  const endX = windowWidth;
  const totalDistance = endX - startX;

  const bgMorning = document.getElementById("bgMorning");
  const bgAfternoon = document.getElementById("bgAfternoon");
  const bgEvening = document.getElementById("bgEvening");
  const bgNight = document.getElementById("bgNight");
  const greetingHeading = document.getElementById("greetingHeading");
  if (!bgMorning || !greetingHeading) return;
  const sunIcon = document.getElementById("sunIcon");
  const moonContainer = document.getElementById("moonContainer");
  const moonShadow = document.getElementById("moonShadow");

  const sunrise = parseKalaTimeSafe(panchangam.solar.sunrise);
  const sunset = parseKalaTimeSafe(panchangam.solar.sunset);
  const solarNoon = parseKalaTimeSafe(panchangam.solar.solar_noon);
  let mRise = parseKalaTimeSafe(panchangam.lunar.moonrise);
  let mSet = parseKalaTimeSafe(panchangam.lunar.moonset);
  // "—" means the event falls outside this panchang day: with no moonset
  // the Moon stays up past next sunrise; with no moonrise it was already up
  // before this sunrise.
  if (sunrise && mRise && !mSet) { mSet = new Date(sunrise); mSet.setDate(mSet.getDate() + 1); }
  if (sunrise && mSet && !mRise) { mRise = new Date(sunrise); mRise.setHours(mRise.getHours() - 12); }
  const isDaytime = !!(sunrise && sunset && now >= sunrise && now <= sunset);

  bgMorning.classList.remove("active");
  bgAfternoon.classList.remove("active");
  bgEvening.classList.remove("active");
  bgNight.classList.remove("active");

  if (sunrise && sunset && solarNoon) {
    if (now >= sunrise && now <= sunset) {
      sunIcon.style.display = "block";
      const p = (now - sunrise) / (sunset - sunrise);
      sunIcon.style.transform = `translateX(${startX + p * totalDistance}px)`;
    } else {
      sunIcon.style.display = "none";
    }

    // "Evening" used to only exist for 2 hours *after* sunset, so "Afternoon"
    // ran all the way from solar noon to sunset itself — on a day with a
    // ~6:40 PM sunset that meant "Good Afternoon" was still showing at
    // 6:13 PM, well past when it reads as evening to anyone looking at the
    // clock. Evening is now a window centered on sunset (2h before to 2h
    // after) instead of only trailing it, so Afternoon ends earlier.
    const eveningStart = new Date(sunset.getTime() - 2 * 60 * 60 * 1000);
    const eveningEnd = new Date(sunset.getTime() + 2 * 60 * 60 * 1000);
    if (now >= sunrise && now < solarNoon) {
      bgMorning.classList.add("active"); applyTheme("day"); greetingHeading.innerText = dict[lang].goodMorning;
    } else if (now >= solarNoon && now < eveningStart) {
      bgAfternoon.classList.add("active"); applyTheme("day"); greetingHeading.innerText = dict[lang].goodAfternoon;
    } else if (now >= eveningStart && now < eveningEnd) {
      bgEvening.classList.add("active"); applyTheme("night"); greetingHeading.innerText = dict[lang].goodEvening;
    } else {
      bgNight.classList.add("active"); applyTheme("night"); greetingHeading.innerText = dict[lang].goodNight;
    }
  }

  if (mRise && mSet) {
    if (mSet <= mRise) {
      if (now < mSet) mRise.setDate(mRise.getDate() - 1);
      else mSet.setDate(mSet.getDate() + 1);
    }
    if (now >= mRise && now <= mSet) {
      moonContainer.style.display = "block";
      moonContainer.classList.toggle("moon-daytime", isDaytime);
      const p = (now - mRise) / (mSet - mRise);
      moonContainer.style.transform = `translateX(${startX + p * totalDistance}px)`;
      let illumination = panchangam.lunar.moon_illumination;
      if (illumination === undefined) illumination = 50;
      // Clamp to a minimum visible sliver — real illumination near Amavasya
      // (e.g. Dvitiya at ~2%) is astronomically correct but renders as
      // indistinguishable from fully dark at this icon size once antialiasing
      // and the daytime dimming are factored in. Trades a little precision
      // right at the extremes for the icon always reading as a moon, not a
      // flat circle that looks broken.
      const visualIllumination = Math.max(10, Math.min(90, illumination));
      moonShadow.style.transform = `translateX(${visualIllumination}%)`;
    } else {
      moonContainer.style.display = "none";
    }
  } else {
    moonContainer.style.display = "none";
  }

  // Collect every Kala whose window contains the current moment \u2014 not just
  // the first match \u2014 so overlapping Kalas (e.g. Rahu Kalam intersecting
  // Gulika Kalam) are all highlighted at once instead of only one of them.
  // Yesterday's panchangam is checked too (dayOffset -1): its "(+1)"
  // periods fall before today's sunrise and are the ones running then.
  const isKalaActive = (time, dayOffset) => {
    if (!time || time === "---" || time === "\u2014") return false;
    return time.split(" / ").some((p) => {
      const parts = p.split(" \u2013 ").length === 2 ? p.split(" \u2013 ") : p.split(" - ");
      return parts.length === 2 && checkTimeInRange(now, parts[0], parts[1], dayOffset);
    });
  };
  const prevKalas = prevPanchangam ? getSpecialKalas(prevPanchangam) : [];
  const activeKalas = getSpecialKalas(panchangam).filter((kala) =>
    isKalaActive(kala.time, 0) ||
    prevKalas.some((pk) => pk.id === kala.id && pk.time.includes("(+1)") && isKalaActive(pk.time, -1))
  );

  document.querySelectorAll(".time-spoke").forEach(s => s.classList.remove("active-kala"));
  document.querySelectorAll(".kala-item").forEach(i => i.classList.remove("active-modal-kala"));

  const viewedDate = document.getElementById("selectedDate")?.value;
  const d = new Date();
  const todayStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const isToday = viewedDate === todayStr;

  if (isToday) {
    for (const kala of activeKalas) {
      const activeSpoke = document.querySelector(`.time-spoke[data-id="${kala.id}"]`);
      if (activeSpoke) activeSpoke.classList.add("active-kala");
      const activeModalItem = document.getElementById(`modal-kala-${kala.id}`);
      if (activeModalItem) activeModalItem.classList.add("active-modal-kala");
    }
  }
}

function applyTheme(theme) {
  const navbar = document.getElementById("mainNavbar");
  const bookCard = document.getElementById("panchangamCard");
  const logoImg = document.querySelector(".logo-img");
  const omLogoImg = document.querySelector(".om-logo-img");
  if (!navbar) return;
  if (theme === "day") {
    navbar.classList.add("day-theme"); navbar.classList.remove("night-theme");
    bookCard.classList.add("day-theme"); bookCard.classList.remove("night-theme");
    if (logoImg) logoImg.setAttribute("src", "/static/Logo.webp");
    if (omLogoImg) omLogoImg.setAttribute("src", "/static/Om_Logo.webp");
  } else {
    navbar.classList.add("night-theme"); navbar.classList.remove("day-theme");
    bookCard.classList.add("night-theme"); bookCard.classList.remove("day-theme");
    if (logoImg) logoImg.setAttribute("src", "/static/Logo_White.webp");
    if (omLogoImg) omLogoImg.setAttribute("src", "/static/Om_Logo_White.webp");
  }
}

function renderExtDayNight(sectionTitle, dayPeriods, nightPeriods, lang, dict) {
  const rows = (periods) => periods.map(p => `
    <tr class="${p.good ? "ext-good" : "ext-bad"}"><td>${p.start}–${p.end}</td><td>${p.name[lang]}</td></tr>`).join("");
  return `
    <div class="ext-section-title">${sectionTitle}</div>
    <div class="ext-daynight">
      <div><div class="ext-subhead">${dict[lang].extDay}</div><table class="ext-table">${rows(dayPeriods)}</table></div>
      <div><div class="ext-subhead">${dict[lang].extNight}</div><table class="ext-table">${rows(nightPeriods)}</table></div>
    </div>`;
}

function renderExtHora(horas, lang, dict) {
  const half = Math.ceil(horas.length / 2);
  const rows = (list) => list.map(h => `<tr><td>${h.start}–${h.end}</td><td>${h.name[lang]}</td></tr>`).join("");
  return `
    <div class="ext-section-title">${dict[lang].extHora}</div>
    <div class="ext-daynight">
      <div><table class="ext-table">${rows(horas.slice(0, half))}</table></div>
      <div><table class="ext-table">${rows(horas.slice(half))}</table></div>
    </div>`;
}

function renderExtGraha(positions, lang, dict) {
  const order = ["sun", "moon", "mars", "mercury", "jupiter", "venus", "saturn", "rahu", "ketu"];
  const rows = order.map(key => {
    const p = positions[key];
    if (!p) return "";
    const flags = [
      p.retrograde ? `<span class="ext-flag" title="${dict[lang].extRetrograde}">R</span>` : "",
      p.combust ? `<span class="ext-flag" title="${dict[lang].extCombust}">C</span>` : "",
    ].join(" ");
    const riseSet = p.rise ? `${p.rise} / ${p.set}` : "—";
    return `<tr><td>${p.name[lang]}</td><td>${p.rashi[lang]} ${p.degree.toFixed(1)}°</td><td>${flags}</td><td>${riseSet}</td></tr>`;
  }).join("");
  return `
    <div class="ext-section-title">${dict[lang].extGraha}</div>
    <table class="ext-table ext-graha-table">
      <tr><th></th><th></th><th></th><th>${dict[lang].extRiseSet}</th></tr>
      ${rows}
    </table>`;
}

function renderExtMisc(ext, lang, dict) {
  const specialDays = [];
  if (ext.special_days.ekadashi) specialDays.push(dict[lang].extEkadashi);
  if (ext.special_days.pournami) specialDays.push(dict[lang].extPournami);
  if (ext.special_days.amavasya) specialDays.push(dict[lang].extAmavasya);
  const specialDaysHtml = specialDays.length
    ? `<div class="ext-note">${dict[lang].extSpecialDay} ${specialDays.join(", ")}</div>` : "";
  const bhadraHtml = ext.bhadra ? `<div class="ext-note ext-warn">${dict[lang].extBhadra}</div>` : "";
  return `
    <div class="ext-section-title">${dict[lang].extLagna}</div>
    <div class="ext-note">${ext.lagna.rashi[lang]} ${ext.lagna.degree.toFixed(1)}°</div>
    <div class="ext-section-title">${dict[lang].extNakshatraInfo}</div>
    <table class="ext-table">
      <tr><td>${dict[lang].extLord}</td><td>${ext.nakshatra_info.lord[lang]}</td></tr>
      <tr><td>${dict[lang].extDeity}</td><td>${ext.nakshatra_info.deity[lang]}</td></tr>
      <tr><td>${dict[lang].extGana}</td><td>${ext.nakshatra_info.gana[lang]}</td></tr>
      <tr><td>${dict[lang].extNadi}</td><td>${ext.nakshatra_info.nadi[lang]}</td></tr>
    </table>
    <div class="ext-section-title">${dict[lang].extDishaShoola}</div>
    <div class="ext-note">${ext.disha_shoola[lang]}</div>
    ${specialDaysHtml}
    ${bhadraHtml}`;
}

function renderFestivalBanner(ext, lang, dict) {
  if (!ext || !ext.festivals || !ext.festivals.length) return "";
  // Shown on every civil day the festival's tithi touches (it can span
  // two), so the exact tithi start/end time is displayed alongside the
  // name rather than silently implying this is the one "correct" day.
  const items = ext.festivals.map(f => {
    const tithiLine = f.tithi_start && f.tithi_end
      ? `<div class="book-festival-tithi">${dict[lang].lblThidhi} ${f.tithi_start} – ${f.tithi_end}</div>`
      : "";
    return `<div class="book-festival-item"><div class="book-festival-name">${f.name[lang]}</div>${tithiLine}</div>`;
  }).join("");
  return `<div class="book-festival-banner"><div class="book-festival-label">${dict[lang].extFestival}</div>${items}</div>`;
}

function populateModal(data, lang, dict) {
  if (!data) return;
  const specialKalas = getSpecialKalas(data);
  const kalasHtml = specialKalas.map(k => `
    <div class="kala-item" id="modal-kala-${k.id}">
      <span class="kala-name">${k.name[lang]}</span>
      <span>${k.time.split(" / ").join("<br>")}</span>
    </div>`).join("");
  const ext = data.extended;
  const extLeftHtml = ext ? `
    ${renderExtDayNight(dict[lang].extChoghadiya, ext.choghadiya.day, ext.choghadiya.night, lang, dict)}
    ${renderExtHora(ext.hora, lang, dict)}
    ${renderExtDayNight(dict[lang].extGowri, ext.gowri_panchangam.day, ext.gowri_panchangam.night, lang, dict)}
  ` : "";
  const extRightHtml = ext ? `${renderExtGraha(ext.graha_positions, lang, dict)}${renderExtMisc(ext, lang, dict)}` : "";

  const panchangamLeftPageHeading = dict[lang].modalTimingsKalas || "Timings & Kalas";
  const sunriseLabel = dict[lang].sunrise || "Sunrise";
  const sunsetLabel = dict[lang].sunset || "Sunset";
  const monriseLabel = dict[lang].moonrise || "Moonrise";
  const moonsetLabel = dict[lang].moonset || "Moonset";
  const masamLabel = dict[lang].lblMasamAmanta;

  // Right-page labels — respect current language
  const rightHeading = dict[lang].modalPanchangamDetails || "Panchangam Details";
  const lblSamvatsara = (dict[lang].lblSamvatsaram || "Samvatsaram:").replace(/:$/, "");
  const lblAyanam = (dict[lang].lblAyanam || "Ayanam:").replace(/:$/, "");
  const lblRithuvu = (dict[lang].lblRithuvu || "Rithuvu:").replace(/:$/, "");
  const lblPaksham = (dict[lang].lblPaksham || "Paksham:").replace(/:$/, "");
  const lblThidhi = (dict[lang].lblThidhi || "Thidhi:").replace(/:$/, "");
  const lblVaram = (dict[lang].lblVaram || "Varam:").replace(/:$/, "");
  const lblNakshatram = (dict[lang].lblNakshatram || "Nakshatram:").replace(/:$/, "");
  const lblYogam = dict[lang].lblYogam || "Yogam";
  const lblKaranam = dict[lang].lblKaranam || "Karanam";

  const left = document.getElementById("panchangamLeftPage");
  const right = document.getElementById("panchangamRightPage");
  if (!left || !right) return;

  const timingsHtml = `
    <div class="kala-item"><span class="kala-name">${sunriseLabel}</span><span>${data.solar.sunrise}</span></div>
    <div class="kala-item"><span class="kala-name">${sunsetLabel}</span><span>${data.solar.sunset}</span></div>
    <div class="kala-item"><span class="kala-name">${monriseLabel}</span><span>${data.lunar.moonrise}</span></div>
    <div class="kala-item"><span class="kala-name">${moonsetLabel}</span><span>${data.lunar.moonset}</span></div>`;

  left.innerHTML = `
    <div style="text-align:center; color:#800000; font-weight:900; margin-bottom:5px; font-size:16px;">${data.date}</div>
    <h2 style="color:#5a3d06; text-align:center; margin-bottom:10px; border-bottom:2px solid #d4af37; padding-bottom:5px;">${panchangamLeftPageHeading}</h2>
    <div class="kala-grid" style="margin-bottom:10px;">${timingsHtml}</div>
    <div class="kala-grid">${kalasHtml}</div>
    ${extLeftHtml}`;

  right.innerHTML = `
    <div style="text-align:center; color:#800000; font-weight:900; margin-bottom:5px; font-size:16px;">${data.date}</div>
    <h2 style="color:#5a3d06; text-align:center; margin-bottom:10px; border-bottom:2px solid #d4af37; padding-bottom:5px;">${rightHeading}</h2>
    ${renderFestivalBanner(ext, lang, dict)}
    <table class="book-table">
      <tr><td>${lblSamvatsara}</td><td>${data.general.samvatsara[lang]}</td></tr>
      <tr><td>${lblAyanam}</td><td>${data.general.ayanam[lang]}</td></tr>
      <tr><td>${lblRithuvu}</td><td>${data.general.ritu[lang]}</td></tr>
      <tr><td>${masamLabel}</td><td>${data.general.masa[lang]}</td></tr>
      <tr><td>${lblPaksham}</td><td>${data.general.paksha[lang]}</td></tr>
      <tr><td>${lblThidhi}</td><td>${data.general.tithi[lang]} ${data.general.tithi_till[lang]}, ${dict[lang].extThen} ${data.general.tithi_next[lang]}</td></tr>
      <tr><td>${lblVaram}</td><td>${data.general.varam[lang]}</td></tr>
      <tr><td>${lblNakshatram}</td><td>${data.general.nakshatra[lang]} ${data.general.nakshatra_till[lang]}</td></tr>
      <tr><td>${lblYogam}</td><td>${data.general.yoga[lang]} ${data.general.yoga_till[lang]}</td></tr>
      <tr><td>${lblKaranam}</td><td>${data.general.karana[lang]} ${data.general.karana_till[lang]}</td></tr>
    </table>
    ${extRightHtml}`;

  // Every open should start scrolled to the top, not wherever the reader
  // left off last time — innerHTML reassignment above doesn't reliably
  // reset scroll position in every browser, so do it explicitly.
  left.scrollTop = 0;
  right.scrollTop = 0;
}
